import { differenceInCalendarDays, isValid, parseISO } from 'date-fns'

/**
 * Fechas de negociación: parseo tolerante y aritmética que no puede devolver `NaN`.
 *
 * ## Por qué existe
 *
 * `currentState.stateAt` NO está guardado como fecha: es un string, y en producción viene en el
 * formato de `Date.prototype.toString()` —«Fri May 02 2025 15:35:57 GMT-0600 (…)»— en las 188
 * negociaciones. `parseISO` no lo entiende, así que hace falta el segundo intento.
 *
 * Y cuando NINGUNO entiende el valor, la cuenta da `NaN`. Eso llegó a la pantalla: la tarjeta del
 * panel de clientes mostraba **«Duración: NaN día(s)»**, porque su guarda era `days != null` y
 * `NaN` no es `null`. Un `NaN` se propaga callado por toda la aritmética y sólo aparece al
 * renderizar, que es el peor lugar para enterarse.
 *
 * Por eso `daysSince` devuelve `null` y no un número: obliga a decidir qué mostrar cuando no hay
 * fecha, en vez de dejar que el fallo se imprima solo.
 */

/** Parsea ISO primero y el formato legacy después. `null` si no es una fecha. */
export function validNegotiationDate(value?: string | Date | null): Date | null {
  if (!value) return null
  if (value instanceof Date) return isValid(value) ? value : null

  const isoDate = parseISO(value)
  if (isValid(isoDate)) return isoDate

  const legacyDate = new Date(value)
  return isValid(legacyDate) ? legacyDate : null
}

/**
 * Días calendario transcurridos desde `value`, o `null` si no hay una fecha válida.
 *
 * Nunca devuelve `NaN`: ése es el punto.
 */
export function daysSince(value?: string | Date | null, from: Date = new Date()): number | null {
  const date = validNegotiationDate(value)
  if (!date) return null

  const days = differenceInCalendarDays(from, date)
  return Number.isNaN(days) ? null : days
}
